"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

type Msg = { role: "user" | "assistant"; content: string };

const SUGGESTIONS = [
  "未経験でも本当に大丈夫ですか？",
  "どんな人が向いていますか？",
  "1か月で何ができるようになりますか？",
  "選考はどう進みますか？",
];

// bot のアイコン。会社ロゴではなく代表本人の顔写真を出す（記事のプロフィール欄と同じ画像）。
const AVATAR_SRC =
  "https://kxikdvizkazpullzodsw.supabase.co/storage/v1/object/public/media/people/numakura.jpg";

const GREETING =
  "代表の沼倉です、と言いたいところですが、私は公開インタビューをもとに答えるAIです。会社のことや採用のことなら、思っていることをそのまま答えます。";

export default function AskNumakura() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fabRef = useRef<HTMLButtonElement>(null);

  // 鍵が入っていなければ、そもそもボタンを出さない。
  useEffect(() => {
    let alive = true;
    fetch("/api/ask")
      .then((r) => r.json())
      .then((d) => alive && setEnabled(Boolean(d?.enabled)))
      .catch(() => alive && setEnabled(false));
    return () => {
      alive = false;
    };
  }, []);

  // 開いたら入力欄へ。閉じたら元のボタンへ戻す。
  useEffect(() => {
    if (open) {
      setMessages((m) => (m.length ? m : [{ role: "assistant", content: GREETING }]));
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
    fabRef.current?.focus();
  }, [open]);

  // Esc で閉じる
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // 新しい発言が増えたら下まで送る
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = useCallback(
    async (text: string) => {
      const q = text.trim();
      if (!q || busy) return;
      const next: Msg[] = [...messages, { role: "user", content: q }];
      setMessages(next);
      setInput("");
      setBusy(true);
      try {
        const res = await fetch("/api/ask", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // 挨拶は文脈に要らないので落とす
          body: JSON.stringify({
            messages: next.filter((m) => m.content !== GREETING),
          }),
        });
        const data = await res.json().catch(() => ({}));
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            content:
              data?.answer ||
              data?.error ||
              "うまく答えられませんでした。時間をおいてお試しください。",
          },
        ]);
      } catch {
        setMessages((m) => [
          ...m,
          { role: "assistant", content: "通信に失敗しました。時間をおいてお試しください。" },
        ]);
      } finally {
        setBusy(false);
        inputRef.current?.focus();
      }
    },
    [busy, messages],
  );

  if (enabled === false || enabled === null) return null;

  return (
    <>
      {/* 起動ボタン */}
      {!open && (
        <button
          ref={fabRef}
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2.5 rounded-full bg-navy-900 py-3 pl-3 pr-5 text-white shadow-cardHover transition-transform hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent md:bottom-7 md:right-7"
          aria-haspopup="dialog"
        >
          <span className="inline-flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-white">
            <Image src={AVATAR_SRC} alt="" width={32} height={32} className="h-8 w-8 object-cover" />
          </span>
          <span className="text-left leading-tight">
            <span className="block text-[13px] font-bold">代表に聞いてみる</span>
            <span className="block text-[10px] tracking-wide text-navy-200">沼倉bot（AI）</span>
          </span>
          <MessageCircle className="h-4 w-4 text-accent" aria-hidden />
        </button>
      )}

      {/* パネル */}
      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="false"
          aria-label="代表への質問（AI）"
          className="fixed inset-x-3 bottom-3 z-50 flex max-h-[80vh] flex-col overflow-hidden rounded-xl2 bg-white shadow-cardHover ring-1 ring-line md:inset-x-auto md:right-7 md:bottom-7 md:h-[560px] md:max-h-[calc(100vh-6rem)] md:w-[380px]"
        >
          {/* ヘッダー */}
          <div className="flex items-start gap-3 bg-navy-900 px-4 py-3.5 text-white">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white">
              <Image src={AVATAR_SRC} alt="" width={36} height={36} className="h-9 w-9 object-cover" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold leading-tight">沼倉bot</p>
              <p className="mt-0.5 text-[11px] leading-snug text-navy-200">
                代表・沼倉隆平の公開インタビューをもとに答えるAIです。本人ではありません。
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="閉じる"
              className="-mr-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-navy-200 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>

          {/* 会話 */}
          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-mist px-4 py-4">
            {messages.map((m, i) => (
              <div
                key={i}
                className={m.role === "user" ? "flex justify-end" : "flex justify-start"}
              >
                <p
                  className={
                    m.role === "user"
                      ? "max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-navy-700 px-3.5 py-2.5 text-[13px] leading-relaxed text-white"
                      : "max-w-[90%] whitespace-pre-wrap rounded-2xl rounded-bl-sm bg-white px-3.5 py-2.5 text-[13px] leading-relaxed text-ink ring-1 ring-line"
                  }
                >
                  {m.content}
                </p>
              </div>
            ))}

            {busy && (
              <div className="flex justify-start">
                <p className="inline-flex items-center gap-2 rounded-2xl rounded-bl-sm bg-white px-3.5 py-2.5 text-[13px] text-muted ring-1 ring-line">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  考えています
                </p>
              </div>
            )}

            {messages.length <= 1 && !busy && (
              <div className="pt-1">
                <p className="mb-2 text-[11px] font-semibold tracking-wide text-muted">
                  よくある質問
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => send(s)}
                      className="rounded-full bg-white px-3 py-1.5 text-[12px] text-navy-700 ring-1 ring-line transition-colors hover:bg-navy-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 入力 */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send(input);
            }}
            className="border-t border-line bg-white px-3 py-3"
          >
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  // Enter で送信、Shift+Enter で改行。日本語変換中は送らない。
                  if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    send(input);
                  }
                }}
                rows={1}
                maxLength={400}
                placeholder="気になることを聞いてください"
                aria-label="質問を入力"
                className="max-h-28 min-h-[44px] flex-1 resize-none rounded-xl border border-line bg-white px-3 py-2.5 text-[13px] leading-relaxed text-ink outline-none transition-colors placeholder:text-navy-300 focus:border-accent"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                aria-label="送信"
                className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent text-white transition-opacity disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              >
                <Send className="h-4 w-4" aria-hidden />
              </button>
            </div>
            <p className="mt-2 text-[10px] leading-snug text-muted">
              AIの回答です。条件や選考の確約はできません。正確なことは
              <a href="/recruit" className="mx-0.5 underline underline-offset-2 hover:text-navy-700">
                面談
              </a>
              で直接お聞きください。
            </p>
          </form>
        </div>
      )}
    </>
  );
}
